import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Building2, CheckCircle2 } from "lucide-react";
import { ALL_PARTNER_MODULES, submitApplication, type PartnerModule } from "@/lib/partner/partnerAccounts";
import { toast } from "sonner";

export const Route = createFileRoute("/partners/register")({
  head: () => ({
    meta: [
      { title: "Request Partner Access · VerseSport" },
      { name: "description", content: "Solicita acceso corporativo al portal VerseSport para tu organización." },
    ],
  }),
  component: PartnerRegisterPage,
});

const INDUSTRIES = ["Broadcast Media", "Sports League", "Beverage", "Telecom", "Financial Services", "Retail / CPG", "Other"];
const COMPANY_TYPES = ["Enterprise", "Agency", "Startup", "Non-profit"];

function PartnerRegisterPage() {
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    company_name: "", contact_name: "", corporate_email: "", phone: "", country: "",
    industry: INDUSTRIES[0], company_type: COMPANY_TYPES[0], role: "",
    requested_modules: ["executive_dashboard", "campaign_performance"] as PartnerModule[],
    nda: false,
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMod = (m: PartnerModule) =>
    set("requested_modules", form.requested_modules.includes(m) ? form.requested_modules.filter((x) => x !== m) : [...form.requested_modules, m]);

  const submit = () => {
    if (!form.nda) { toast.error("Debes aceptar el NDA"); return; }
    submitApplication({
      company_name: form.company_name,
      contact_name: form.contact_name,
      corporate_email: form.corporate_email,
      phone: form.phone,
      country: form.country,
      industry: form.industry,
      company_type: form.company_type,
      role: form.role,
      requested_modules: form.requested_modules,
      nda_accepted_at: new Date().toISOString(),
    });
    setDone(true);
    toast.success("Solicitud enviada");
  };

  if (done) {
    return (
      <div className="mx-auto max-w-md px-4 py-12 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-[oklch(0.82_0.24_145)]" />
        <h1 className="mt-4 text-2xl font-black">Verification Submitted</h1>
        <p className="mt-2 text-sm text-muted-foreground">Tu solicitud está <b>pending_review</b>. Nuestro equipo te contactará en 1-2 días hábiles.</p>
        <Link to="/partners" className="mt-6 inline-block rounded-md border border-border px-4 py-2 text-sm font-bold">Volver al Portal</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-primary">
        <Building2 className="h-4 w-4" /> Request Partner Access · Step {step}/4
      </div>
      <h1 className="mb-6 text-2xl font-black">Registro Corporativo</h1>
      <div className="space-y-4 rounded-2xl border border-border bg-background/60 p-5">
        {step === 1 && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Company Name" value={form.company_name} onChange={(v) => set("company_name", v)} required />
            <Field label="Contact Name" value={form.contact_name} onChange={(v) => set("contact_name", v)} required />
            <Field label="Corporate Email" type="email" value={form.corporate_email} onChange={(v) => set("corporate_email", v)} required />
            <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
            <Field label="Country" value={form.country} onChange={(v) => set("country", v)} required />
          </div>
        )}
        {step === 2 && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Select label="Industry" value={form.industry} options={INDUSTRIES} onChange={(v) => set("industry", v)} />
            <Select label="Company Type" value={form.company_type} options={COMPANY_TYPES} onChange={(v) => set("company_type", v)} />
            <Field label="Your Role" value={form.role} onChange={(v) => set("role", v)} required />
          </div>
        )}
        {step === 3 && (
          <div>
            <div className="mb-2 text-xs font-bold">Requested Access Modules</div>
            <div className="grid grid-cols-2 gap-2">
              {ALL_PARTNER_MODULES.map((m) => (
                <label key={m} className="flex items-center gap-2 rounded-md border border-border bg-background/50 px-2 py-1.5 text-xs">
                  <input type="checkbox" checked={form.requested_modules.includes(m)} onChange={() => toggleMod(m)} />
                  {m.replace(/_/g, " ")}
                </label>
              ))}
            </div>
          </div>
        )}
        {step === 4 && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Aceptas el NDA y los términos de uso del Portal de Socios.</p>
            <label className="flex items-center gap-2 text-xs font-bold">
              <input type="checkbox" checked={form.nda} onChange={(e) => set("nda", e.target.checked)} />
              Acepto el NDA y términos
            </label>
          </div>
        )}
        <div className="flex justify-between pt-2">
          <button disabled={step === 1} onClick={() => setStep(step - 1)} className="rounded-md border border-border px-3 py-1.5 text-xs font-bold disabled:opacity-40">Atrás</button>
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} className="rounded-md bg-primary px-3 py-1.5 text-xs font-black text-primary-foreground">Siguiente</button>
          ) : (
            <button onClick={submit} className="rounded-md bg-primary px-3 py-1.5 text-xs font-black text-primary-foreground">Enviar Solicitud</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field(props: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean }) {
  return (
    <label className="block text-xs font-bold">{props.label}{props.required && " *"}
      <input type={props.type ?? "text"} required={props.required} value={props.value} onChange={(e) => props.onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
    </label>
  );
}
function Select(props: { label: string; value: string; options: string[]; onChange: (v: string) => void }) {
  return (
    <label className="block text-xs font-bold">{props.label}
      <select value={props.value} onChange={(e) => props.onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
        {props.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}